import { useEffect, useRef, useState } from 'react';
import useDockIndicator from '../../hooks/ui/useDockIndicator';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import useViewportSafeAreaLayoutEpoch from '../../hooks/ui/useViewportSafeAreaLayoutEpoch';
import { isOrientationSettling } from '../../utils/ui/viewportSafeAreaLayoutEpoch';
import { useVisualViewportFrame } from '../../hooks/ui/useVisualViewportFrame';
import { preventTouchFocusScroll } from '../../utils/ui/mobileListRowInteraction';
import { usePullRefreshHandler } from '../../ui/pullRefresh/PullRefreshContext';
import { overlayLayers } from '../../ui/overlayLayers';
import { buildTabs } from './bottomTabInventory';
import { primitives } from '../../ui';
import BottomTabButton from './BottomTabButton';
import { useResponsiveLayout } from '../../hooks/ui/useResponsiveLayout';
import useReducedMotion from '../../hooks/ai/useReducedMotion';
import { setAiDockSlot, useAiDockSlot } from '../ai/aiDockSlot';

/**
 * Is the floating bottom nav pill on screen? Every shell renders BottomTabBar
 * with `isMobile={isMobile || isTablet}` (< 1024px) — but several floating
 * bottom-anchored elements used to key their "float above the nav" branch on
 * the PHONE-ONLY isMobile (< 768px), so on TABLET (768–1023) they took the
 * desktop path (sticky, low z) and slid UNDER this z=100 pill (the Org save
 * bar bug, 2026-07-23). Any bottom-anchored floating element must branch on
 * THIS hook, never on a phone-only boolean, so its notion of "nav present"
 * can't drift from the bar's actual render predicate.
 */
export function useBottomNavPresent() {
  const { isMobile, isTablet } = useResponsiveLayout();
  return isMobile || isTablet;
}

const TAB_HEIGHT_PX = 64;
/** Minimum pill lift when safe-area is 0 (web / responsive preview). */
const TAB_BOTTOM_OFFSET_MIN_PX = 8;
/** Gap above the home-indicator clearance. */
const TAB_BOTTOM_OFFSET_ABOVE_SAFE_PX = 2;
/** Cap how much of the iOS home-indicator inset we reserve under the pill.
 *  A standalone PWA reports a full ~34px bottom inset; reserving ALL of it lifts
 *  this FLOATING pill ~36px off the edge (that full reserve is meant for edge-to-
 *  edge bars, not a detached pill) — which read as "pill sits too high."
 *  Why the cap (not MIN_PX): prior "lower it" tweaks changed MIN_PX, but on device
 *  the max() always picked the bigger (2px + inset) branch, so MIN_PX only ever
 *  affected the browser. The cap is the only lever that moves the pill on device.
 *  CALIBRATION (don't go below ~24): a cap of 16 sat the pill at ~18px from the
 *  edge — INSIDE iOS's home-indicator edge-protection zone (~20-21pt), so a tap
 *  low on the centered tab woke the home bar instead of firing the control. 26
 *  sits the pill at ~28px: clear of that zone, still ~8px below the "too high"
 *  full-reserve. Bump up by ~4 if a tap still occasionally wakes the home bar.
 *  2026-10-30 (founder, matched to Instagram's floating bar): 26 -> 22, pill ~24px off the edge — still
 *  above the ~20-21pt edge-protection zone; re-check a low tap on the centre tab on device.
 *  Safari *browser* needs no separate term (see TAB_BOTTOM_CSS below) — a
 *  `position: fixed; bottom` offset there already measures from the top of
 *  Safari's own toolbar, not the page's full layout height. */
const TAB_SAFE_AREA_RESERVE_CAP_PX = 22;
/** Do NOT re-add a toolbar/lvh-vs-dvh term here. It shipped once (a
 *  `calc(100lvh - 100dvh + …)` "toolbar gap") and lifted the pill ~48px too
 *  high in Safari *browser* mode. Evidence (founder `?vpdebug=1`, iPhone
 *  Safari browser, 2026-09-25): env(safe-area-inset-bottom) 0, dvh 796, lvh
 *  836 — the term added the full 40px lvh/dvh gap on top of an offset that
 *  is ALREADY measured from the top of Safari's own bottom toolbar, because
 *  `position: fixed; bottom` resolves against the *visible* (dvh) viewport,
 *  not the layout (lvh) one. That's a double lift, not a real correction. */
/** Pill bottom — home bar on device, modest floor on web/browser. */
const TAB_BOTTOM_CSS = `max(${TAB_BOTTOM_OFFSET_MIN_PX}px, calc(${TAB_BOTTOM_OFFSET_ABOVE_SAFE_PX}px + min(env(safe-area-inset-bottom, 0px), ${TAB_SAFE_AREA_RESERVE_CAP_PX}px)))`;
// Vignette + tap zone: viewport bottom → pill top (fade ends with the pill).
const CHROME_HEIGHT_CSS = `calc(${TAB_HEIGHT_PX}px + ${TAB_BOTTOM_CSS})`;

// Total clearance any sticky/fixed mobile element must leave at the bottom
// of the viewport so it never sits behind the floating BottomTabBar. Composed
// of: tab height (64) + max pill bottom offset (~42 on iPhone) + visual gap (16).
const BOTTOM_TAB_CLEARANCE_PX = TAB_HEIGHT_PX + TAB_BOTTOM_OFFSET_MIN_PX + TAB_BOTTOM_OFFSET_ABOVE_SAFE_PX + 34 + 16;

/** Gap between a floating element (e.g. bulk action bar) and the nav pill top. */
// Founder-tuned 8px (was 12, mockup 17, 2026-07-23): every floating pill
// (save bar, bulk pills, toasts) rides this — they move together.
const ABOVE_TAB_GAP_PX = 8;
// CSS `bottom` for a floating element that should sit just above the nav pill.
// Derived from the pill's OWN position (TAB_BOTTOM_CSS + height + gap) so the
// two stay in sync, and uses env() exactly once — no baked-in safe-area
// constant — so the bar rides a consistent ~12px above the pill on every
// device instead of the inflated, double-counted BOTTOM_TAB_CLEARANCE_PX gap.
const FLOATING_ABOVE_TAB_BOTTOM_CSS = `calc(${TAB_BOTTOM_CSS} + ${TAB_HEIGHT_PX}px + ${ABOVE_TAB_GAP_PX}px)`;

/** Gap between a scroll surface's last row and the nav pill top. */
const CONTENT_ABOVE_TAB_GAP_PX = 16;
// Bottom padding for scrollable mobile content so its last row clears the
// floating pill by ~16px. Derived from the pill's OWN position — TAB_BOTTOM_CSS
// already caps the home-indicator inset at 16px, so this does NOT re-add the full
// env(safe-area-inset-bottom) on top. The old `104px + env(safe-area-inset-bottom)`
// did, reserving ~56px of dead scroll space below content on a notched iPhone.
// + (100lvh − 100dvh): the shell is min-height 100lvh (load-bearing, see AppShell), so in
// Safari BROWSER its last ~40px sit behind Safari's own toolbar. Pad the CONTENT for that
// hidden sliver (0 in the PWA / when Safari's bar collapses) — never the pill itself.
// No AI-button reserve: the button sits in this bar's row on phone/tablet (KAN-991), and on
// desktop it steps aside while scrolling down instead of padding every page (KAN-1144).
const MOBILE_CONTENT_BOTTOM_PAD_CSS = `calc(${TAB_BOTTOM_CSS} + ${TAB_HEIGHT_PX}px + ${CONTENT_ABOVE_TAB_GAP_PX}px + (100lvh - 100dvh))`;

// Stable identity for the default `hiddenNavKeys`. A `= []` default parameter
// mints a NEW array every render, and this value is a dep of the dock-indicator
// layout effect — an inline default made that effect fire on every render,
// setState a fresh object, and re-render forever ("Maximum update depth
// exceeded") on the tablet dock. Keep this module-level.
const EMPTY_NAV_KEYS = Object.freeze([]);
/** RD-ROLE-006 — see NavigationNavItems' identical constant for why this
 *  must be module-level rather than an inline `= {}` default. */
const EMPTY_BADGE_COUNTS = Object.freeze({});

/** Swallow stray taps on the chrome dead zone; tab pill stays interactive. */
function absorbChromePointer(e) {
  if (e.target.closest('[data-cvx-bottom-tab], [data-cvx-ai-dock-slot]')) return;
  preventTouchFocusScroll(e);
}

export { TAB_HEIGHT_PX, BOTTOM_TAB_CLEARANCE_PX, FLOATING_ABOVE_TAB_BOTTOM_CSS, MOBILE_CONTENT_BOTTOM_PAD_CSS, TAB_BOTTOM_CSS, ABOVE_TAB_GAP_PX };

export default function BottomTabBar({
  role,
  activeTab,
  /** Tapped-but-uncommitted tab: moves ONLY the selection card at the tap (KAN-900). */
  pendingNavTab = null,
  onTabSelect,
  isMobile,
  // Pill tabs no longer include AI Hub; prop kept so shells can keep passing it.
  aiEnabled: _aiEnabled = false,
  hiddenNavKeys = EMPTY_NAV_KEYS,
  /** KAN-1022: platform ROOT only — swaps the Audit pill for Cancellations. Fails closed (absent = Audit). */
  isRootPlatform = false,
  /** When true, render in-flow (inside MobileNavShell) instead of portaling to body. */
  attachToSurface = false,
  /** Push-nav open — tabs must not switch; dismiss scrim handles taps. */
  interactionLocked = false,
  /** RD-ROLE-006 — { [tabKey]: count }. Only AdminHome/BillingHome pass this;
   *  every other role's call site omits it, so this bar is byte-identical to
   *  before for worker/supervisor/platform. */
  badgeCounts = EMPTY_BADGE_COUNTS,
}) {
  const intl = useIntl();
  const { hasHandler: hasRefreshHandler } = usePullRefreshHandler();
  const viewportLayoutEpoch = useViewportSafeAreaLayoutEpoch();
  // Local debounced visualViewport remount key. iOS Safari leaves
  // env(safe-area-inset-bottom) stale after a keyboard close in browser mode,
  // which drops the tab bar behind the URL bar. Bumping a local key after the
  // resize sequence settles forces this nav to remount and re-read the inset.
  // Scoped to BottomTabBar specifically so we don't disturb LogJob, AI panel,
  // or any other epoch consumer that holds local state.
  const [vvRemountKey, setVvRemountKey] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return undefined;
    let timerId = 0;
    const onResize = () => {
      // Mid orientation settle: Safari fires visualViewport resize while insets
      // are still wrong — remounting here freezes the pill at a bad bottom offset.
      if (isOrientationSettling()) return;
      clearTimeout(timerId);
      timerId = setTimeout(() => setVvRemountKey((k) => k + 1), 250);
    };
    window.visualViewport.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timerId);
      window.visualViewport.removeEventListener('resize', onResize);
    };
  }, []);

  // Hide the floating pill while the keyboard is open. iOS surfaces this
  // position:fixed bottom pill at the keyboard's TOP edge, so it floats up into
  // the modal/content area and visibly shifts as the keyboard animates — and
  // you don't need the nav while typing. Fade it out (standard mobile feel).
  // Keyboard-CLOSED overlays still keep the pill dimmed behind their scrim
  // (deliberate — a preview modal's dim sits above bottomTabChrome z=100).
  const { keyboardOpen } = useVisualViewportFrame(isMobile);

  const { isMobile: phoneBand, isTablet: tabletBand, isNarrowPhone } = useResponsiveLayout();
  const { occupied: aiOccupied } = useAiDockSlot();
  const reducedMotion = useReducedMotion();
  // EXPERIMENT (founder, 2026-07-24): the compact dock runs on PHONE too —
  // IG-style icon dock with the morph chip. The labeled-pill branch below is
  // intentionally KEPT; reverting the phone to labels = restore the condition
  // `tabletBand && !phoneBand` here. Phone uses tighter metrics (44px targets)
  // so five items + the active chip fit an iPhone SE (320px).
  const dockMode = tabletBand ? 'dock' : phoneBand ? 'bar' : null;
  // KAN-994: phones >= 360 get the full-width "pill" bar (16px gutters, tabs
  // fill it, capped at 302 (~54px tabs) so big phones stay tight and centered). SE (<360) and
  // the tablet dock keep their existing geometry.
  const pillBar = dockMode === 'bar' && !isNarrowPhone;
  // Tablet switch animation: ONE tinted chip glides from the old tab to the
  // new one (left/width tween on a single small node — smooth and cheap since
  // always-labeled items never change width). Items keep transparent
  // backgrounds in dock mode; the indicator carries the tint.
  const navRef = useRef(null);
  const badgeKey = Object.keys(badgeCounts).sort().map((key) => `${key}:${badgeCounts[key] || 0}`).join('|');
  // vvRemountKey / viewportLayoutEpoch remount the <nav> (see its `key`), and the
  // dynamic 5th slot swaps the active label — all listed so the chip re-measures.
  const indicatorRef = useRef(null);
  // role / hidden keys / remount epochs re-place the card with NO glide.
  useDockIndicator(navRef, indicatorRef, {
    mode: dockMode,
    activeTab,
    targetTab: pendingNavTab || activeTab,
    resetKey: `${role}|${vvRemountKey}|${viewportLayoutEpoch}|${hiddenNavKeys.join(',')}|${isRootPlatform}`,
    reducedMotion,
    stretch: dockMode ? 1.12 : 0,
    deps: [badgeKey],
  });

  if (!isMobile) return null;

  // Preview modals sit above the pill behind their dimming scrim (no hide) —
  // EXCEPT while the keyboard is up, when the lifted pill would intrude.
  const navHidden = keyboardOpen;
  const suppressed = interactionLocked || navHidden;
  // The LAST pill is a dynamic slot: on an overflow feature (admin customers /
  // reports / catalog …) it borrows that feature's identity so the bar is never
  // unlit, then reverts. Reflect-only — no memory, by design. Tab COUNT is
  // constant, so the other pills never move. See bottomTabInventory.js.
  const tabs = buildTabs(role, hiddenNavKeys, activeTab, isRootPlatform);
  // TABLET-ONLY compact dock (option C, mockup 18): 768–1023 renders a
  // centered icon dock whose ACTIVE tab inflates into an icon+label chip (the
  // label rises from its phone-pill home under the icon into the side seat).
  // Phone keeps the full-width labeled pill; desktop (≥1024) has no bottom
  // nav at all. Same height + bottom offset as the pill, so the entire
  // clearance system (FLOATING_ABOVE_TAB / content pads) is untouched.
  // Three silhouettes, one chip language (founder-tuned 2026-07-24):
  //  - PHONE  ('bar'):  full-width anchored bar, icon-first, morph chip active
  //  - TABLET ('dock'): centered floating dock, roomier metrics
  //  - DESKTOP: no bottom nav (sidebar) — shells never render this there.
  const compactDock = dockMode !== null;
  // FINAL geometry (founder-tuned on device, 2026-07-24): the phone bar keeps
  // the ORIGINAL airy spacing — full 64px bar, 44px chip floating with generous
  // vertical air, tabs spread evenly (the slim uniform-inset variant was built
  // and REJECTED on sight; don't resurrect it). Tablet keeps the uniform-inset
  // dock. HINTS (whisper labels under resting icons) live on TABLET ONLY —
  // phone is clean icon-only, the name appears in the selected chip.
  // KAN-991: on an SE-class phone sharing the row with the AI button the
  // bar compacts (40px tabs, 2px gap, 6px pads) so both fit; otherwise 44/8/8.
  const compactBar = dockMode === 'bar' && isNarrowPhone && aiOccupied;
  const dockTarget = tabletBand ? 48 : compactBar ? 40 : 44;
  // Tablet: names sit close (founder-tuned off the live dock) — the 12px gap
  // plus 16px pads read as ~44px between neighbors; 4 + 12 lands at ~28px.
  const dockGap = tabletBand ? 4 : compactBar ? 2 : 8;
  // Phone bar, founder-tuned 'a bit less tall' (reserve stays 64). KAN-994 pill: 56px bar,
  // 44px tabs -> 6px inset top/bottom (incl. the nav's 1px border), AI button 56 on the same centre line.
  const BAR_HEIGHT_PX = pillBar ? 56 : 60;
  const dockSidePad = tabletBand ? 8 : 12;
  const dockChipPad = tabletBand ? 12 : 14;
  // Tablet (founder-final): every tab shows its name TO THE RIGHT of the icon,
  // always — a fully-labeled dock. Selection just moves the tinted chip; item
  // widths never change, so nothing shifts. (The under-icon hint variant was
  // tried and replaced by this.) Phone keeps the label-promotion morph.
  const alwaysLabeled = dockMode === 'dock';
  const DOCK_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const dockTransition = reducedMotion ? 'none' : `240ms ${DOCK_EASE}`;

  const chromeLabel = intl.formatMessage({
    id: 'aria.bottomTabBar',
    defaultMessage: 'Bottom navigation',
  });

  const chrome = (
    <div
      key={`${viewportLayoutEpoch}-${vvRemountKey}`}
      data-cvx-bottom-chrome
      {...(attachToSurface ? { 'data-cvx-bottom-chrome-surface': true } : {})}
      {...(interactionLocked ? { 'data-cvx-bottom-chrome-locked': true } : {})}
      aria-hidden={suppressed || undefined}
      onPointerDownCapture={suppressed ? undefined : absorbChromePointer}
      style={{
        height: CHROME_HEIGHT_CSS,
        zIndex: overlayLayers.bottomTabChrome,
        // Keyboard up: the whole chrome (AI slot included) sinks 12px while it fades,
        // and rises back on show. Transform + opacity only; reduced motion = instant.
        transition: reducedMotion ? 'none' : 'opacity 200ms ease, transform 200ms ease',
        transform: navHidden ? 'translateY(12px)' : 'translateY(0)',
        ...(navHidden ? { opacity: 0, pointerEvents: 'none' } : null),
      }}
    >
      <div data-cvx-bottom-chrome-vignette aria-hidden={true} />
      <div
        data-cvx-bottom-row
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: dockMode === 'bar'
            ? `calc(${TAB_BOTTOM_CSS} + ${(TAB_HEIGHT_PX - BAR_HEIGHT_PX) / 2}px)`
            : TAB_BOTTOM_CSS,
          height: dockMode === 'bar' ? BAR_HEIGHT_PX : TAB_HEIGHT_PX,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none',
          zIndex: 1,
          ...(pillBar ? {
            paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
            boxSizing: 'border-box',
          } : null),
        }}
      >
      <nav
        ref={navRef}
        data-cvx-bottom-tab
        role="tablist"
        aria-label={chromeLabel}
        aria-hidden={suppressed || undefined}
        style={{
          position: 'relative',
          left: 'auto',
          right: 'auto',
          bottom: 'auto',
          transform: 'none',
          pointerEvents: 'auto',
          height: TAB_HEIGHT_PX,
          ...(dockMode === 'dock' ? {
            width: 'fit-content',
            maxWidth: 'calc(100vw - 32px)',
            alignItems: 'center',
            gap: dockGap,
            paddingLeft: dockSidePad,
            paddingRight: dockSidePad,
          } : null),
          ...(dockMode === 'bar' ? {
            // Founder-final: the phone pill HUGS its content like the tablet
            // dock — centered fit-content with small end pads — instead of a
            // full-width bar with stranded space at the rounded ends. The pill
            // breathes as the chip morphs (items animate width; the container
            // follows per-frame).
            height: BAR_HEIGHT_PX,
            width: 'fit-content',
            maxWidth: 'calc(100vw - 32px)',
            alignItems: 'center',
            gap: dockGap,
            paddingLeft: compactBar ? 6 : 8,
            paddingRight: compactBar ? 6 : 8,
            ...(pillBar ? {
              flex: '1 1 auto',
              minWidth: 0,
              width: 'auto',
              maxWidth: 302,
              justifyContent: 'stretch',
              gap: 4,
              paddingLeft: 6,
              paddingRight: 6,
            } : null),
          } : null),
        }}
      >
        {compactDock && (
          <span
            ref={indicatorRef}
            aria-hidden
            {...(dockMode === 'bar' ? { 'data-bar-indicator': true } : { 'data-dock-indicator': true })}
            style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              height: dockTarget,
              borderRadius: pillBar ? 999 : dockTarget / 2,
              ...primitives.navSelectedCard,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}
        {tabs.map((tab) => (
          <BottomTabButton
            key={tab.slotKey || tab.key}
            tab={tab}
            activeTab={activeTab}
            badgeCounts={badgeCounts}
            compactDock={compactDock}
            alwaysLabeled={alwaysLabeled}
            dockMode={dockMode}
            dockTarget={dockTarget}
            pillBar={pillBar}
            dockChipPad={dockChipPad}
            dockTransition={dockTransition}
            reducedMotion={reducedMotion}
            suppressed={suppressed}
            hasRefreshHandler={hasRefreshHandler}
            onTabSelect={onTabSelect}
          />
        ))}
      </nav>
      <span data-cvx-ai-dock-slot ref={setAiDockSlot} style={{ display: 'flex', pointerEvents: 'auto' }} />
      </div>
    </div>
  );

  if (attachToSurface || typeof document === 'undefined') return chrome;
  return createPortal(chrome, document.body);
}
